import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../api/axios';

interface UserInfo {
    _id: string;
    email: string;
    name: string;
    role: string;
    status: string;
}

interface TokenValidationResponse {
    success: boolean;
    data: {
        valid: boolean;
        user: UserInfo;
    };
}

interface PasswordSetupResponse {
    success: boolean;
    data: {
        token: string;
    };
    message?: string;
}

interface ResendEmailResponse {
    success: boolean;
    message?: string;
}

const PasswordSetup = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [tokenValid, setTokenValid] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Invalid password setup link');
                setValidatingToken(false);
                return;
            }

            try {
                const response = await api.get<TokenValidationResponse>(`/auth/validate-setup-token/${token}`);
                const data = response.data;

                if (data.success && data.data.valid) {
                    setTokenValid(true);
                    setUserInfo(data.data.user);
                } else {
                    setError('Password setup link is invalid or has expired');
                }
            } catch (err) {
                console.error('Token validation error:', err);
                setError('Failed to validate setup link');
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setIsLoading(false);
            return;
        }

        // Validate password strength
        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            setIsLoading(false);
            return;
        }

        try {
            const response = await api.post<PasswordSetupResponse>('/auth/setup-password', {
                token,
                password: formData.password
            });

            const data = response.data;

            if (data.success) {
                setSuccess(true);
                localStorage.setItem('token', data.data.token);

                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError(data.message || 'Failed to set up password');
            }
        } catch (err: any) {
            console.error('Password setup error:', err);
            setError(err.response?.data?.message || 'Network error. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendEmail = async () => {
        if (!userInfo?.email) return;

        try {
            const response = await api.post<ResendEmailResponse>('/auth/resend-password-setup', {
                email: userInfo.email
            });

            const data = response.data;
            if (data.success) {
                setError('');
                alert('Password setup email resent successfully');
            } else {
                setError(data.message || 'Failed to resend email');
            }
        } catch (err: any) {
            console.error('Resend email error:', err);
            setError(err.response?.data?.message || 'Failed to resend email');
        }
    };

    const getPasswordStrength = () => {
        const password = formData.password;
        const checks = [
            { test: password.length >= 8, label: '8+ chars' },
            { test: /[A-Z]/.test(password), label: 'Upper' },
            { test: /[a-z]/.test(password), label: 'Lower' },
            { test: /[0-9]/.test(password), label: 'Number' },
            { test: /[^A-Za-z0-9]/.test(password), label: 'Special' }
        ];
        return checks;
    };

    // Loading state while validating token
    if (validatingToken) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Validating setup link...</p>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="card shadow-lg" style={{ maxWidth: '400px', width: '100%' }}>
                    <div className="card-body text-center p-5">
                        <CheckCircle className="text-success mb-4" size={64} />
                        <h2 className="card-title mb-3">Password Set Successfully!</h2>
                        <p className="text-muted mb-4">
                            Welcome to Yodelpop! You'll be redirected to the login page shortly.
                        </p>
                        <div className="d-flex align-items-center justify-content-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span className="text-primary">Redirecting...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Invalid token state
    if (!tokenValid) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                <div className="card shadow-lg" style={{ maxWidth: '400px', width: '100%' }}>
                    <div className="card-body text-center p-5">
                        <AlertTriangle className="text-danger mb-4" size={64} />
                        <h2 className="card-title mb-3">Invalid Setup Link</h2>
                        <p className="text-muted mb-4">{error}</p>
                        {userInfo?.email && (
                            <button
                                onClick={handleResendEmail}
                                className="btn btn-link text-primary"
                            >
                                Resend setup email
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light p-3">
            <div className="card shadow-lg" style={{ maxWidth: '500px', width: '100%' }}>
                <div className="card-body p-5">
                    {/* Header */}
                    <div className="text-center mb-4">
                        <h1 className="h2 fw-bold mb-3">Set Up Your Password</h1>
                        {userInfo && (
                            <p className="text-muted">
                                Welcome {userInfo.name}! Please create a secure password for your account.
                            </p>
                        )}
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {/* Password Field */}
                        <div className="mb-3">
                            <label htmlFor="password" className="form-label fw-medium">
                                New Password
                            </label>
                            <div className="input-group">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>

                            {/* Password Strength Indicator */}
                            {formData.password && (
                                <div className="bg-light rounded p-3 mt-2">
                                    <div className="d-flex flex-wrap gap-3">
                                        {getPasswordStrength().map((check, index) => (
                                            <div
                                                key={index}
                                                className={`d-flex align-items-center ${check.test ? 'text-success' : 'text-muted'
                                                    }`}
                                            >
                                                <span className="me-1">
                                                    {check.test ? '✓' : '•'}
                                                </span>
                                                <small>{check.label}</small>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div className="mb-4">
                            <label htmlFor="confirmPassword" className="form-label fw-medium">
                                Confirm Password
                            </label>
                            <div className="input-group">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className="form-control"
                                    placeholder="Confirm your password"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>

                            {/* Password Match Indicator */}
                            {formData.confirmPassword && (
                                <small className={`d-block mt-1 ${formData.password === formData.confirmPassword
                                        ? 'text-success'
                                        : 'text-danger'
                                    }`}>
                                    {formData.password === formData.confirmPassword
                                        ? '✓ Passwords match'
                                        : '✗ Passwords do not match'
                                    }
                                </small>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading || formData.password !== formData.confirmPassword || !formData.password}
                            className="btn btn-primary w-100 py-2"
                        >
                            {isLoading ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span>Setting up password...</span>
                                </div>
                            ) : (
                                'Set Up Password'
                            )}
                        </button>

                        {/* Help Text */}
                        <div className="text-center mt-3">
                            <small className="text-muted">
                                By setting up your password, you agree to our Terms of Service and Privacy Policy.
                            </small>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PasswordSetup;